import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../../services/api';
import { useTranslation } from '../../context/LanguageContext';
import './CompanyRegisterPage.css';

function CompanyRegisterPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [saveMessage, setSaveMessage] = useState('');
    const [saveMessageType, setSaveMessageType] = useState(''); // success | error

    const [formData, setFormData] = useState({
        // 기본 정보
        companyName: '',
        companyCode: '',
        schemaName: '',
        businessNumber: '',
        representativeName: '',
        // 연락처 정보
        contactEmail: '',
        contactPhone: '',
        address: '',
        // 구독 정보
        subscriptionPlan: 'BASIC',
        subscriptionStatus: 'TRIAL',
        maxUsers: 10,
        maxEquipments: 20,
        // 관리자 계정 정보
        adminUsername: '',
        adminEmail: '',
        adminPassword: '',
        adminPasswordConfirm: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleCompanyCodeChange = (e) => {
        const value = e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '');
        setFormData(prev => ({
            ...prev,
            companyCode: value,
            schemaName: value || ''
        }));
        if (errors.companyCode) {
            setErrors(prev => ({ ...prev, companyCode: '' }));
        }
    };

    const handleBusinessNumberChange = (e) => {
        const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
        let formatted = value;
        if (value.length > 3 && value.length <= 5) {
            formatted = `${value.slice(0, 3)}-${value.slice(3)}`;
        } else if (value.length > 5) {
            formatted = `${value.slice(0, 3)}-${value.slice(3, 5)}-${value.slice(5)}`;
        }
        setFormData(prev => ({
            ...prev,
            businessNumber: formatted
        }));
        if (errors.businessNumber) {
            setErrors(prev => ({ ...prev, businessNumber: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.companyName.trim()) {
            newErrors.companyName = t('pages.platform.companyRegister.validation.nameRequired');
        }
        if (!formData.companyCode.trim()) {
            newErrors.companyCode = t('pages.platform.companyRegister.validation.codeRequired');
        } else if (formData.companyCode.length < 2 || formData.companyCode.length > 20) {
            newErrors.companyCode = t('pages.platform.companyRegister.validation.codeLength');
        }
        if (!formData.representativeName.trim()) {
            newErrors.representativeName = t('pages.platform.companyRegister.validation.ceoRequired');
        }
        if (formData.businessNumber && !/^\d{3}-\d{2}-\d{5}$/.test(formData.businessNumber)) {
            newErrors.businessNumber = t('pages.platform.companyRegister.validation.businessNumberInvalid');
        }

        if (formData.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactEmail)) {
            newErrors.contactEmail = t('pages.platform.companyRegister.validation.contactEmailInvalid');
        }
        if (formData.contactPhone && !/^\d{2,3}-\d{3,4}-\d{4}$/.test(formData.contactPhone)) {
            newErrors.contactPhone = t('pages.platform.companyRegister.validation.contactPhoneInvalid');
        }

        if (!formData.maxUsers || formData.maxUsers < 1) {
            newErrors.maxUsers = t('pages.platform.companyRegister.validation.maxUsersMin');
        }
        if (!formData.maxEquipments || formData.maxEquipments < 1) {
            newErrors.maxEquipments = t('pages.platform.companyRegister.validation.maxEquipmentsMin');
        }

        if (!formData.adminUsername.trim()) {
            newErrors.adminUsername = t('pages.platform.companyRegister.validation.adminUsernameRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminUsername)) {
            newErrors.adminUsername = t('pages.platform.companyRegister.validation.adminEmailInvalid');
        }
        if (!formData.adminEmail.trim()) {
            newErrors.adminEmail = t('pages.platform.companyRegister.validation.adminEmailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
            newErrors.adminEmail = t('pages.platform.companyRegister.validation.adminEmailInvalid');
        }
        if (!formData.adminPassword) {
            newErrors.adminPassword = t('pages.platform.companyRegister.validation.adminPasswordRequired');
        } else if (formData.adminPassword.length < 8) {
            newErrors.adminPassword = t('pages.platform.companyRegister.validation.adminPasswordLength');
        }
        if (formData.adminPassword !== formData.adminPasswordConfirm) {
            newErrors.adminPasswordConfirm = t('pages.platform.companyRegister.validation.adminPasswordMismatch');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setSaveMessage('');
        setSaveMessageType('');

        try {
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');
            const response = await fetch(`${getApiBaseUrl()}/platform/companies`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    companyName: formData.companyName,
                    companyCode: formData.companyCode,
                    schemaName: formData.schemaName,
                    businessNumber: formData.businessNumber || null,
                    representativeName: formData.representativeName,
                    contactEmail: formData.contactEmail || null,
                    contactPhone: formData.contactPhone || null,
                    address: formData.address || null,
                    subscriptionPlan: formData.subscriptionPlan,
                    subscriptionStatus: formData.subscriptionStatus,
                    maxUsers: parseInt(formData.maxUsers),
                    maxEquipments: parseInt(formData.maxEquipments),
                    adminUsername: formData.adminUsername,
                    adminEmail: formData.adminEmail,
                    adminPassword: formData.adminPassword
                })
            });

            const result = await response.json();

            if (result.success) {
                setSaveMessage(t('pages.platform.companyRegister.success'));
                setSaveMessageType('success');
                setTimeout(() => {
                    navigate('/platform/companies');
                }, 1500);
            } else {
                setSaveMessage(result.error || t('pages.platform.companyRegister.fail'));
                setSaveMessageType('error');
            }
        } catch (error) {
            console.error('회사 등록 실패:', error);
            setSaveMessage(t('pages.platform.companyRegister.error'));
            setSaveMessageType('error');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/platform/companies');
    };

    return (
        <div className="company-register-page">
            <form className="company-register-form" onSubmit={handleSubmit}>
                {/* 기본 정보 */}
                <div className="form-section">
                    <h3 className="section-title">{t('pages.platform.companyRegister.sectionBasic')}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.name')} <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                className={`form-input ${errors.companyName ? 'error' : ''}`}
                                value={formData.companyName}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.name')}
                            />
                            {errors.companyName && <span className="error-message">{errors.companyName}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.code')} <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyCode"
                                className={`form-input ${errors.companyCode ? 'error' : ''}`}
                                value={formData.companyCode}
                                onChange={handleCompanyCodeChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.code')}
                                maxLength={20}
                            />
                            {errors.companyCode && <span className="error-message">{errors.companyCode}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.schema')}</label>
                            <input
                                type="text"
                                name="schemaName"
                                className="form-input readonly"
                                value={formData.schemaName}
                                readOnly
                                placeholder={t('pages.platform.companyRegister.placeholder.schema')}
                            />
                            <span className="form-hint">{t('pages.platform.companyRegister.schemaHint')}</span>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.ceo')} <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="representativeName"
                                className={`form-input ${errors.representativeName ? 'error' : ''}`}
                                value={formData.representativeName}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.ceo')}
                            />
                            {errors.representativeName && <span className="error-message">{errors.representativeName}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.businessNumber')}</label>
                            <input
                                type="text"
                                name="businessNumber"
                                className={`form-input ${errors.businessNumber ? 'error' : ''}`}
                                value={formData.businessNumber}
                                onChange={handleBusinessNumberChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.businessNumber')}
                            />
                            {errors.businessNumber && <span className="error-message">{errors.businessNumber}</span>}
                        </div>
                    </div>
                </div>

                {/* 연락처 정보 */}
                <div className="form-section">
                    <h3 className="section-title">{t('pages.platform.companyRegister.sectionContact')}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.contactEmail')}</label>
                            <input
                                type="email"
                                name="contactEmail"
                                className={`form-input ${errors.contactEmail ? 'error' : ''}`}
                                value={formData.contactEmail}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.contactEmail')}
                            />
                            {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.contactPhone')}</label>
                            <input
                                type="text"
                                name="contactPhone"
                                className={`form-input ${errors.contactPhone ? 'error' : ''}`}
                                value={formData.contactPhone}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.contactPhone')}
                            />
                            {errors.contactPhone && <span className="error-message">{errors.contactPhone}</span>}
                        </div>

                        <div className="form-group form-group-full">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.address')}</label>
                            <input
                                type="text"
                                name="address"
                                className="form-input"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.address')}
                            />
                        </div>
                    </div>
                </div>

                {/* 구독 정보 */}
                <div className="form-section">
                    <h3 className="section-title">{t('pages.platform.companyRegister.sectionSubscription')}</h3>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.plan')}</label>
                            <div className="select-container">
                                <select
                                    name="subscriptionPlan"
                                    className="form-select"
                                    value={formData.subscriptionPlan}
                                    onChange={handleChange}
                                >
                                    <option value="BASIC">{t('pages.platform.companyRegister.plan.basic')}</option>
                                    <option value="STANDARD">{t('pages.platform.companyRegister.plan.standard')}</option>
                                    <option value="PREMIUM">{t('pages.platform.companyRegister.plan.premium')}</option>
                                    <option value="ENTERPRISE">{t('pages.platform.companyRegister.plan.enterprise')}</option>
                                </select>
                                <span className="material-symbols-outlined select-icon">expand_more</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">{t('pages.platform.companyRegister.fields.status')}</label>
                            <div className="select-container">
                                <select
                                    name="subscriptionStatus"
                                    className="form-select"
                                    value={formData.subscriptionStatus}
                                    onChange={handleChange}
                                >
                                    <option value="TRIAL">{t('pages.platform.companyRegister.status.trial')}</option>
                                    <option value="ACTIVE">{t('pages.platform.companyRegister.status.active')}</option>
                                    <option value="SUSPENDED">{t('pages.platform.companyRegister.status.suspended')}</option>
                                </select>
                                <span className="material-symbols-outlined select-icon">expand_more</span>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.maxUsers')} <span className="required">*</span>
                            </label>
                            <input
                                type="number"
                                name="maxUsers"
                                className={`form-input ${errors.maxUsers ? 'error' : ''}`}
                                value={formData.maxUsers}
                                onChange={handleChange}
                                min="1"
                            />
                            {errors.maxUsers && <span className="error-message">{errors.maxUsers}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.maxEquipments')} <span className="required">*</span>
                            </label>
                            <input
                                type="number"
                                name="maxEquipments"
                                className={`form-input ${errors.maxEquipments ? 'error' : ''}`}
                                value={formData.maxEquipments}
                                onChange={handleChange}
                                min="1"
                            />
                            {errors.maxEquipments && <span className="error-message">{errors.maxEquipments}</span>}
                        </div>
                    </div>
                </div>

                {/* 관리자 계정 */}
                <div className="form-section">
                    <h3 className="section-title">{t('pages.platform.companyRegister.sectionAdmin')}</h3>
                    <p className="section-description">{t('pages.platform.companyRegister.adminDescription')}</p>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.adminUsername')} <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                name="adminUsername"
                                className={`form-input ${errors.adminUsername ? 'error' : ''}`}
                                value={formData.adminUsername}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.adminUsername')}
                                autoComplete="off"
                            />
                            {errors.adminUsername && <span className="error-message">{errors.adminUsername}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.adminEmail')} <span className="required">*</span>
                            </label>
                            <input
                                type="email"
                                name="adminEmail"
                                className={`form-input ${errors.adminEmail ? 'error' : ''}`}
                                value={formData.adminEmail}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.adminEmail')}
                                autoComplete="off"
                            />
                            {errors.adminEmail && <span className="error-message">{errors.adminEmail}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.adminPassword')} <span className="required">*</span>
                            </label>
                            <input
                                type="password"
                                name="adminPassword"
                                className={`form-input ${errors.adminPassword ? 'error' : ''}`}
                                value={formData.adminPassword}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.adminPassword')}
                                autoComplete="new-password"
                            />
                            {errors.adminPassword && <span className="error-message">{errors.adminPassword}</span>}
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                {t('pages.platform.companyRegister.fields.adminPasswordConfirm')} <span className="required">*</span>
                            </label>
                            <input
                                type="password"
                                name="adminPasswordConfirm"
                                className={`form-input ${errors.adminPasswordConfirm ? 'error' : ''}`}
                                value={formData.adminPasswordConfirm}
                                onChange={handleChange}
                                placeholder={t('pages.platform.companyRegister.placeholder.adminPasswordConfirm')}
                                autoComplete="new-password"
                            />
                            {errors.adminPasswordConfirm && <span className="error-message">{errors.adminPasswordConfirm}</span>}
                        </div>
                    </div>
                </div>

                {/* 버튼 영역 */}
                <div className="form-actions">
                    {saveMessage && (
                        <span className={`save-message ${saveMessageType === 'error' ? 'error' : 'success'}`}>
                            {saveMessage}
                        </span>
                    )}
                    <button type="button" className="btn-cancel" onClick={handleCancel} disabled={loading}>
                        {t('pages.platform.companyRegister.cancel')}
                    </button>
                    <button type="submit" className="btn-submit" disabled={loading}>
                        {loading ? t('pages.platform.companyRegister.submitting') : t('pages.platform.companyRegister.submit')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default CompanyRegisterPage;
